/**
 * client/viewmodel/psview.ts — the PlayerState + events the camera and viewmodel read each frame, with a
 * debug override (window.__vm) so tests can pose the viewmodel before the weapon state machine exists.
 *
 * OWNER: VIEWMODEL+CAMERA agent. Debug only: when no override is set this is a pass-through.
 */
import type { PlayerState } from '@shared/types';
import type { WeaponDef } from '@shared/weapons/defs';
import type { ClientWorld, FrameEvent } from '../types';

export interface VmDebug {
  /** Fields merged over world.predicted (e.g. { weaponAnim, weapon, adsFrac }). */
  override: Partial<PlayerState> | null;
  /** Events injected for the next frame (played once, like world.frameEvents). */
  inject: FrameEvent[];
  /** Freeze presentation time (anims/sway still advance with dt = 0). */
  paused: boolean;
  /** Read-only status for tests (camera + viewmodel write here). */
  stats: Record<string, unknown>;
}

export const vmDebug: VmDebug = { override: null, inject: [], paused: false, stats: {} };

let frameInjected: FrameEvent[] = [];
let merged: PlayerState | null = null;

/** Called once per frame by the camera (first presentation module): moves injected events into this frame. */
export function beginPresentationFrame(): void {
  frameInjected = vmDebug.inject;
  vmDebug.inject = [];
}

export function frameEvents(world: ClientWorld): readonly FrameEvent[] {
  return frameInjected.length ? [...world.frameEvents, ...frameInjected] : world.frameEvents;
}

export function effectivePs(world: ClientWorld): PlayerState {
  const o = vmDebug.override;
  if (!o) return world.predicted;
  merged = Object.assign(merged ?? ({} as PlayerState), world.predicted, o);
  return merged;
}

/**
 * Render-rate bob inputs. T6 runs pmove (and so PM_Footsteps' bobCycle) every client frame, so the bob is smooth at
 * any frame rate. Our cmds are a fixed 60 Hz and world.view only lerps origin/viewHeight, so the camera and
 * viewmodel bob from bobCycle/xy velocity lerped between the last two predicted cmd states with world.view.alpha
 * (the same alpha session.ts uses for origin). bobCycle is a u8 that wraps (and runs backwards going down a
 * ladder), so it is lerped along the shortest signed delta and left fractional (getBobCycle accepts that).
 */
export class PredictedLerp {
  bobCycle = 0;
  readonly velocity: [number, number, number] = [0, 0, 0];
  private cmdTime = Number.NaN;
  private spawn = -1;
  private prevBob = 0;
  private curBob = 0;
  private readonly prevVel: [number, number, number] = [0, 0, 0];
  private readonly curVel: [number, number, number] = [0, 0, 0];

  /** ps = this frame's predicted state, alpha = world.view.alpha (0..1 between the previous cmd state and ps). */
  update(ps: Pick<PlayerState, 'bobCycle' | 'velocity' | 'commandTime' | 'spawnCount'>, alpha: number): void {
    const v = ps.velocity;
    if (ps.spawnCount !== this.spawn || !(ps.commandTime >= this.cmdTime) || ps.commandTime - this.cmdTime > 100) {
      // first frame, respawn, or a time jump: no previous state to lerp from
      this.spawn = ps.spawnCount;
      this.prevBob = ps.bobCycle;
      this.prevVel[0] = v[0]; this.prevVel[1] = v[1]; this.prevVel[2] = v[2];
    } else if (ps.commandTime !== this.cmdTime) {
      this.prevBob = this.curBob;
      this.prevVel[0] = this.curVel[0]; this.prevVel[1] = this.curVel[1]; this.prevVel[2] = this.curVel[2];
    } // same cmd (e.g. re-predicted after a snapshot): keep prev, take the corrected current values
    this.cmdTime = ps.commandTime;
    this.curBob = ps.bobCycle;
    this.curVel[0] = v[0]; this.curVel[1] = v[1]; this.curVel[2] = v[2];
    const a = alpha > 0 ? (alpha < 1 ? alpha : 1) : 0;
    const d = ((((this.curBob - this.prevBob) % 256) + 384) % 256) - 128; // shortest signed u8 delta
    this.bobCycle = this.prevBob + d * a;
    for (let i = 0; i < 3; i++) this.velocity[i] = this.prevVel[i] + (this.curVel[i] - this.prevVel[i]) * a;
  }
}

/** Shared by camera (updates it first each frame) and viewmodel (reads it). */
export const predictedLerp = new PredictedLerp();

/** Numeric raw weapon-file field (0 when absent). */
export function rawNum(def: WeaponDef, key: string): number {
  const v = def.raw[key];
  return typeof v === 'number' ? v : 0;
}

declare global {
  interface Window { __vm?: unknown }
}
